
import React, { useEffect, useState } from "react";
import { db } from "../../firebaseClient";
import { exportToExcel } from "../export/excel";
import type { ActivityLogEntry } from "../services/activityLog";
import { useAuth } from "../context/AuthContext";
import { canViewAuditLog } from "../config/roles";
import { formatDate } from "../utils/dateUtils";
import { Modal } from "../components/Modal";
import { DEPARTMENTS, DEPT_CODE_TO_NAME } from "../constants/departments";

export function AuditLogPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'SYSTEM' | 'APPROVALS' | 'ALL'>('SYSTEM');
  
  // Strict Access Control: KC & AD only
  if (!canViewAuditLog(user?.email)) {
      return (
        <div style={{
            height:'100%', display:'flex', alignItems:'center', justifyContent:'center', 
            flexDirection:'column', color:'var(--danger)'
        }}>
            <h2 style={{margin:0}}>403 Forbidden</h2>
            <p>您的帳號 ({user?.email}) 無權存取稽核紀錄。</p>
        </div>
      );
  }

  const isSuper = true; // Since only KC/AD can enter, they are effectively super here

  return (
      <div style={{height:'100%', display:'flex', flexDirection:'column'}}>
          <div className="row" style={{ borderBottom: '1px solid #333', marginBottom: 16 }}>
            <button 
                className="navbtn" 
                style={{ width: 'auto', borderBottom: activeTab === 'SYSTEM' ? '2px solid #fff' : 'none', color: activeTab === 'SYSTEM' ? '#fff' : 'var(--muted)' }} 
                onClick={() => setActiveTab('SYSTEM')}
            >
                系統紀錄 (System)
            </button>
            <button 
                className="navbtn" 
                style={{ width: 'auto', borderBottom: activeTab === 'APPROVALS' ? '2px solid #fff' : 'none', color: activeTab === 'APPROVALS' ? '#fff' : 'var(--muted)' }} 
                onClick={() => setActiveTab('APPROVALS')}
            >
                簽核紀錄 (Approvals)
            </button>
            {isSuper && (
                <button 
                    className="navbtn" 
                    style={{ width: 'auto', borderBottom: activeTab === 'ALL' ? '2px solid #fff' : 'none', color: activeTab === 'ALL' ? '#fff' : 'var(--muted)' }} 
                    onClick={() => setActiveTab('ALL')}
                >
                    操作日誌 (Detailed)
                </button>
            )}
          </div>
          
          <div style={{flex:1, overflow:'hidden'}}>
              {activeTab === 'SYSTEM' && <UnifiedLogView viewMode="SYSTEM" />}
              {activeTab === 'APPROVALS' && <UnifiedLogView viewMode="APPROVALS" />}
              {activeTab === 'ALL' && isSuper && <UnifiedLogView viewMode="ALL" />}
          </div>
      </div>
  );
}

// ... Rest of the file (UnifiedLogView component) remains the same ...
// Including full content below for completeness as requested in instructions to replace file content.

interface UnifiedViewProps {
    viewMode: 'SYSTEM' | 'APPROVALS' | 'ALL';
}

function UnifiedLogView({ viewMode }: UnifiedViewProps) {
    const [logs, setLogs] = useState<ActivityLogEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedLog, setSelectedLog] = useState<ActivityLogEntry | null>(null);
    const [isManualSearch, setIsManualSearch] = useState(false);
    
    // Date Range (Default: Last 30 Days)
    const [startDate, setStartDate] = useState(() => {
        const d = new Date(); d.setDate(d.getDate() - 30);
        return formatDate(d);
    });
    const [endDate, setEndDate] = useState(() => formatDate(new Date()));

    // Advanced Filters (Only for ALL mode)
    const [moduleFilter, setModuleFilter] = useState("ALL");
    const [deptFilter, setDeptFilter] = useState("ALL");
    const [keywordFilter, setKeywordFilter] = useState("");

    const loadLogs = (useLimit50: boolean = false) => {
        setLoading(true);
        console.log(`[AuditLog] Loading logs. Mode: ${viewMode}, Limit50: ${useLimit50}`);
        
        let q = db.collection("activity_logs").orderBy("createdAt", "desc");

        if (useLimit50) {
            // "Latest 50" Mode
            q = q.limit(50);
        } else {
            // "Date Range Search" Mode
            const startTs = new Date(`${startDate}T00:00:00`);
            const endTs = new Date(`${endDate}T23:59:59`);
            q = q.where("createdAt", ">=", startTs)
                 .where("createdAt", "<=", endTs);
        }
        
        q.get().then(snap => {
            let fetched = snap.docs.map(d => ({ id: d.id, ...d.data() } as ActivityLogEntry));
            console.log(`[AuditLog] Fetched ${fetched.length} raw logs from Firestore`);

            // --- 1. View Mode Pre-Filtering ---
            if (viewMode === 'SYSTEM') {
                // System Logs: Auth events, Login/Logout
                fetched = fetched.filter(l => l.module === 'auth' || l.action === 'login' || l.action === 'logout');
            } else if (viewMode === 'APPROVALS') {
                // Approval Logs: Sign-off actions
                const approvalActions = ['approve', 'reject', 'submit', 'start_flow', 'reset_flow', 'APPROVED', 'REJECTED', 'RETURNED', 'LEGAL_RETURNED', 'SUBMITTED'];
                fetched = fetched.filter(l => 
                    approvalActions.some(act => l.action.toLowerCase().includes(act.toLowerCase())) ||
                    l.module === 'contractReview' || 
                    l.module === 'seal' || 
                    (l.module as string) === 'sealRequest' || 
                    l.module === 'STAMP_REQUEST' ||
                    l.module === 'govCorrespondence'
                );
            }

            // --- 2. User Filters (For ALL mode mainly or Manual Search) ---
            if (!useLimit50) {
                if (moduleFilter !== "ALL") {
                    fetched = fetched.filter(l => l.module === moduleFilter);
                }
                if (deptFilter !== "ALL") {
                    fetched = fetched.filter(l => l.actorDepartment === deptFilter || l.actorDepartment === DEPT_CODE_TO_NAME[deptFilter]);
                }
                if (keywordFilter) {
                    const low = keywordFilter.toLowerCase();
                    fetched = fetched.filter(l => 
                        (l.actorName||"").toLowerCase().includes(low) || 
                        (l.entityTitle||"").toLowerCase().includes(low) ||
                        (l.details||"").toLowerCase().includes(low) ||
                        (l.action||"").toLowerCase().includes(low)
                    );
                }
            }

            // Ensure sorting desc if mixed query affected it
            fetched.sort((a,b) => {
               const ta = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
               const tb = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
               return tb - ta;
            });

            setLogs(fetched);
        }).catch(err => {
            console.error("Fetch logs failed", err);
        }).finally(() => setLoading(false));
    };

    // Auto load on mount or tab change (Default: Latest 50)
    useEffect(() => {
        setIsManualSearch(false);
        loadLogs(true);
    }, [viewMode]); 

    const handleSearch = () => {
        setIsManualSearch(true);
        loadLogs(false);
    };

    const handleExport = () => {
        exportToExcel(`AuditLog_${viewMode}`, logs.map(l => ({
            時間: l.createdAt?.toDate ? l.createdAt.toDate().toLocaleString() : "-",
            模組: l.module,
            動作: l.action,
            操作者: l.actorName,
            部門: l.actorDepartment || "-",
            對象: l.entityTitle || l.entityId,
            摘要: l.details || "",
            IP: l.ip || (l as any).clientIp || l.ipAddress
        })));
    };

    const MODULE_OPTS = [
        "contract", "contractReview", "legalMatter", "seal", "govCorrespondence", "auth", "task", "govProject", "template"
    ];

    // Columns Definition
    const renderColumns = () => {
        if (viewMode === 'SYSTEM') {
            return (
                <>
                    <th style={{ padding: 12 }}>時間</th>
                    <th style={{ padding: 12 }}>動作</th>
                    <th style={{ padding: 12 }}>操作者 (帳號)</th>
                    <th style={{ padding: 12 }}>IP 位址</th>
                    <th style={{ padding: 12 }}>摘要</th>
                </>
            );
        }
        if (viewMode === 'APPROVALS') {
            return (
                <>
                    <th style={{ padding: 12 }}>時間</th>
                    <th style={{ padding: 12 }}>模組</th>
                    <th style={{ padding: 12 }}>動作 / 決定</th>
                    <th style={{ padding: 12 }}>簽核對象</th>
                    <th style={{ padding: 12 }}>操作者</th>
                    <th style={{ padding: 12 }}>IP 位址</th>
                    <th style={{ padding: 12 }}>意見摘要</th>
                </>
            );
        }
        return (
            <>
                <th style={{ padding: 12 }}>時間</th>
                <th style={{ padding: 12 }}>模組 / 動作</th>
                <th style={{ padding: 12 }}>操作者 / 部門</th>
                <th style={{ padding: 12 }}>目標對象</th>
                <th style={{ padding: 12 }}>IP 位址</th>
                <th style={{ padding: 12 }}>摘要</th>
            </>
        );
    };

    const renderRow = (log: ActivityLogEntry) => {
        const timeStr = log.createdAt?.toDate ? log.createdAt.toDate().toLocaleString() : "-";
        
        const displayIp = log.ip || (log as any).clientIp || log.ipAddress || "unknown";
        
        if (viewMode === 'SYSTEM') {
            return (
                <>
                    <td style={{ padding: 12, color: "var(--muted)", whiteSpace:'nowrap' }}>{timeStr}</td>
                    <td style={{ padding: 12 }}><span className="badge">{log.action}</span></td>
                    <td style={{ padding: 12 }}>{log.actorName} <span style={{fontSize:11, color:'#666'}}>({log.userEmail})</span></td>
                    <td style={{ padding: 12, fontFamily:'monospace', color:'#a5b4fc' }}>{displayIp}</td>
                    <td style={{ padding: 12, color:'#ccc' }}>{log.details}</td>
                </>
            );
        }
        if (viewMode === 'APPROVALS') {
            let badgeColor = '#aaa';
            if (log.action.includes('APPROVED')) badgeColor = 'var(--ok)';
            if (log.action.includes('REJECTED') || log.action.includes('RETURNED')) badgeColor = 'var(--danger)';
            if (log.action.includes('SUBMITTED') || log.action.includes('start')) badgeColor = '#fbbf24';

            return (
                <>
                    <td style={{ padding: 12, color: "var(--muted)", whiteSpace:'nowrap' }}>{timeStr}</td>
                    <td style={{ padding: 12 }}>{log.module}</td>
                    <td style={{ padding: 12 }}>
                        <span className="badge" style={{color: badgeColor, borderColor: badgeColor}}>{log.action}</span>
                    </td>
                    <td style={{ padding: 12 }}>{log.entityTitle || log.entityId}</td>
                    <td style={{ padding: 12 }}>{log.actorName}</td>
                    <td style={{ padding: 12, fontFamily:'monospace', color:'#a5b4fc' }}>{displayIp}</td>
                    <td style={{ padding: 12, fontSize:12, color:'#ccc' }}>{log.details}</td>
                </>
            );
        }
        // ALL
        return (
            <>
                <td style={{ padding: 12, color: "var(--muted)", whiteSpace:'nowrap' }}>{timeStr}</td>
                <td style={{ padding: 12 }}>
                    <div className="badge">{log.module}</div>
                    <div style={{marginTop:4, fontSize:12}}>{log.action}</div>
                </td>
                <td style={{ padding: 12 }}>
                    <div>{log.actorName}</div>
                    <div style={{fontSize:11, color:'#666'}}>{log.actorDepartment || '-'}</div>
                </td>
                <td style={{ padding: 12 }}>
                    <div style={{fontSize:13}}>{log.targetType || log.entityType}</div>
                    <div style={{fontSize:11, color:'#a5b4fc', fontFamily:'monospace'}}>{log.entityTitle || log.entityId}</div>
                </td>
                <td style={{ padding: 12, fontSize:12, fontFamily:'monospace', color:'#a5b4fc' }}>
                    {displayIp}
                </td>
                <td style={{ padding: 12, fontSize:12, color:'#ccc', maxWidth: 300, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                    {log.details || JSON.stringify(log.extra)}
                </td>
            </>
        );
    };

    return (
        <div style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
            {/* Filter Section */}
            <div className="section" style={{marginBottom: 16, padding: "12px 16px", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
                    <div style={{fontSize:12, color: isManualSearch ? 'var(--ok)' : 'var(--muted)'}}>
                        {isManualSearch ? "模式：依日期範圍搜尋" : "模式：預設顯示最新 50 筆"}
                    </div>
                </div>
                
                <div style={{display:'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, alignItems:'end'}}>
                    <div style={{gridColumn: 'span 2'}}>
                        <label className="label">日期範圍</label>
                        <div style={{display:'flex', gap:6, alignItems:'center'}}>
                            <input type="date" className="input" value={startDate} onChange={e => setStartDate(e.target.value)} />
                            <span>~</span>
                            <input type="date" className="input" value={endDate} onChange={e => setEndDate(e.target.value)} />
                        </div>
                    </div>
                    
                    {viewMode === 'ALL' && (
                        <>
                            <div>
                                <label className="label">模組</label>
                                <select className="input" value={moduleFilter} onChange={e => setModuleFilter(e.target.value)}>
                                    <option value="ALL">全部</option>
                                    {MODULE_OPTS.map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="label">部門</label>
                                <select className="input" value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
                                    <option value="ALL">全部</option>
                                    {DEPARTMENTS.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="label">關鍵字</label>
                                <input className="input" placeholder="姓名/標題/動作" value={keywordFilter} onChange={e => setKeywordFilter(e.target.value)} />
                            </div>
                        </>
                    )}

                    <div style={{display:'flex', gap:8, gridColumn: viewMode==='ALL' ? 'auto' : 'span 2'}}>
                        <button className="smallbtn" style={{height:42, width:'100%'}} onClick={handleSearch}>搜尋</button>
                        <button className="smallbtn" style={{height:42, background:'#fff', color:'#000'}} onClick={handleExport}>匯出 Excel</button>
                    </div>
                </div>
            </div>

            <div className="audit-log-table-wrapper" style={{flex:1, overflow:'hidden', display:'flex'}}>
                <div className="audit-log-table-scroll" style={{overflow:'auto', width:'100%'}}>
                    <table className="audit-log-table">
                        <thead style={{ background: "#222", textAlign: "left", color: "var(--muted)" }}>
                            <tr>
                                {renderColumns()}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={8} style={{ padding: 20, textAlign: 'center', color: 'var(--muted)' }}>
                                        載入中...
                                    </td>
                                </tr>
                            )}
                            
                            {!loading && logs.length === 0 && (
                                <tr>
                                    <td colSpan={8} style={{ padding: 20, textAlign: 'center', color: 'var(--muted)' }}>
                                        無紀錄 (請檢查日期範圍或系統寫入狀態)
                                    </td>
                                </tr>
                            )}

                            {!loading && logs.length > 0 && logs.map((log, idx) => (
                                <tr 
                                    key={log.id || idx} 
                                    style={{ borderBottom: "1px solid #333", cursor:'pointer' }} 
                                    onClick={() => setSelectedLog(log)}
                                >
                                    {renderRow(log)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {selectedLog && (
                <Modal title="日誌詳情" onClose={() => setSelectedLog(null)} width={800}>
                    <div className="grid">
                        <div className="field"><label className="label">Timestamp</label><div className="p">{selectedLog.createdAt?.toDate?.().toLocaleString()}</div></div>
                        <div className="field"><label className="label">Module / Action</label><div className="p">{selectedLog.module} / {selectedLog.action}</div></div>
                        <div className="field"><label className="label">Actor</label><div className="p">{selectedLog.actorName} ({selectedLog.userEmail})</div></div>
                        <div className="field"><label className="label">IP Address</label><div className="p">{selectedLog.ip || (selectedLog as any).clientIp || selectedLog.ipAddress}</div></div>
                        <div className="field" style={{gridColumn:'1/-1'}}><label className="label">Details</label>
                            <div className="p" style={{background:'rgba(255,255,255,0.05)', padding:10, borderRadius:6}}>{selectedLog.details}</div>
                        </div>
                        {selectedLog.extra && (
                            <div className="field" style={{gridColumn:'1/-1'}}><label className="label">Extra Data</label>
                                <pre style={{fontSize:11, color:'#aaa', background:'#111', padding:10, borderRadius:6, overflow:'auto'}}>
                                    {JSON.stringify(selectedLog.extra, null, 2)}
                                </pre>
                            </div>
                        )}
                    </div>
                    <div className="row" style={{marginTop:20, justifyContent:'flex-end'}}>
                        <button className="btn" style={{width:'auto'}} onClick={() => setSelectedLog(null)}>關閉</button>
                    </div>
                </Modal>
            )}
        </div>
    );
}
